import gsap from 'gsap';
import 'vite/modulepreload-polyfill';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Autoplay, EffectFade, Keyboard, Navigation } from 'swiper/modules';
import { createApp, onMounted, onUnmounted } from 'vue';

import * as AnalyticsData from '~/analytics/analytics-data';
import * as DataDogLogs from '~/lib/datadog/datadog';
import { initSwiper } from '~/utils/swiper';

import { BaseSection } from './base-section';

gsap.registerPlugin(ScrollTrigger);

const sectionName = 'global-banner';

/**
 * Global Banner Section
 *
 * This section displays a global banner carousel with a messages and links.
 *
 * @extends BaseSection
 */
class GlobalBanner extends BaseSection {
  constructor() {
    super(`[data-section-type="${sectionName}"]`);
  }

  init(instanceData) {
    const container = instanceData.container;

    if (!container) {
      DataDogLogs.error(
        '[Allbirds section] - Global Banner - init - Container not found',
        {},
        new Error('Container not found'),
      );
      return;
    }

    createApp({
      name: 'GlobalBannerSection',
      setup() {
        let carousel = null;
        const autoplayDelay = Number(container.getAttribute('data-autoplay-delay'));

        onMounted(() => {
          carousel = initSwiper(container, {
            autoplay: {
              delay: autoplayDelay,
              disableOnInteraction: true,
            },
            effect: 'fade',
            fadeEffect: {
              crossFade: true,
            },
            keyboard: {
              enabled: true,
            },
            loop: true,
            modules: [Autoplay, EffectFade, Navigation, Keyboard],
            slidesPerView: 1,
          });

          if (carousel) {
            document.body.classList.add('global-banner-active');

            // Measure and set the banner height dynamically after layout
            requestAnimationFrame(() => {
              const bannerHeight = container.offsetHeight;
              document.documentElement.style.setProperty(
                '--global-banner-height',
                `${bannerHeight}px`,
              );
            });
          }

          // Hook the analytics events
          AnalyticsData.hookGlobalBannerLinkClicked({ sectionName });
        });

        onUnmounted(() => {
          carousel?.destroy();
          carousel = null;

          document.body.classList.remove('global-banner-active');
          document.body.classList.remove('global-banner-in-view');
        });

        return {};
      },
    }).mount(container);
  }

  setupInViewAnimationTimeline(instanceData) {
    const container = instanceData.container;

    if (!container) {
      DataDogLogs.error(
        '[Allbirds section] - Global Banner - setupInViewAnimationTimeline - Missing elements for in-view animation',
        {},
        new Error('Missing elements for in-view animation'),
      );
      return null;
    }

    const tl = gsap.timeline({
      paused: true,
      scrollTrigger: {
        once: false,
        onEnter: () => {
          if (document.body.style.position === 'fixed') return;

          document.body.classList.add('global-banner-in-view');
        },
        onEnterBack: () => {
          if (document.body.style.position === 'fixed') return;

          document.body.classList.add('global-banner-in-view');
        },
        onLeave: () => {
          if (document.body.style.position === 'fixed') return;

          document.body.classList.remove('global-banner-in-view');
        },
        trigger: container,
      },
    });

    if (!tl) return null;

    return tl;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new GlobalBanner();
});
